import React, { useEffect, useMemo, useState } from 'react';

function getFirst(obj, keys, fallback = '') {
  for (const k of keys) {
    const v = obj?.[k];
    if (v !== undefined && v !== null && v !== '') return v;
  }
  return fallback;
}

function formatDate(v) {
  if (!v) return '';
  try {
    const d = new Date(v);
    if (isNaN(d.getTime())) return String(v);
    return d.toLocaleString();
  } catch {
    return String(v);
  }
}

function computeInviteStatus(inv) {
  try {
    const exp = inv?.expires_at ? new Date(inv.expires_at) : null;
    const now = new Date();
    if (inv?.is_active === false) return 'consumed';
    if (exp && !isNaN(exp.getTime())) return exp < now ? 'expired' : 'active';
  } catch {}
  return inv?.is_active ? 'active' : 'inactive';
}

function normalizeUsersAndInvites(data) {
  if (Array.isArray(data)) {
    const usersList = data.map(r => r.user).filter(Boolean);
    const invs = data.flatMap(r =>
      (r.invites || []).map(inv => ({
        email: r.email,
        ...inv,
        status: computeInviteStatus(inv),
      }))
    );
    return { users: usersList, invitations: invs };
  }
  const usersList = Array.isArray(data?.users) ? data.users : [];
  const invs = Array.isArray(data?.invitations) ? data.invitations : (Array.isArray(data?.invites) ? data.invites : []);
  return { users: usersList, invitations: invs };
}

 // After normalizeUsersAndInvites(...)
const INVITE_LABELS = {
  email: 'Email',
  code: 'Code',
  created_at: 'Created',
  expires_at: 'Expires',
  status: 'Status',
  organization: 'Organization',
  organization_role: 'Organization role',
  used_by: 'Used by'
};

const USER_LABELS = {
  id: 'ID',
  email: 'Email',
  name: 'Name',
  full_name: 'Full name',
  system_role: 'System role',
  organization: 'Organization',
  organization_role: 'Organization role',
  created_at: 'Created'
};

const STATUS_LABELS = {
  expired: 'Expired',
  active: 'Active',
  consumed: 'Consumed',
  inactive: 'Inactive'
};

export default function AdminPanel({ open, onClose }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [users, setUsers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [form, setForm] = useState({
    email: '',
    organization: '',
    organizationRole: '',
    expiresInDays: 14
  });
  const [submitting, setSubmitting] = useState(false);
  const [isDark, setIsDark] = useState(() => {
    return (document.body?.dataset?.agThemeMode || localStorage.getItem('theme')) === 'dark';
  });

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', handleKey);
    const syncTheme = () => {
      setIsDark((document.body?.dataset?.agThemeMode || localStorage.getItem('theme')) === 'dark');
    };
    const interval = setInterval(syncTheme, 400);
    return () => {
      window.removeEventListener('keydown', handleKey);
      clearInterval(interval);
    };
  }, [onClose]);

  useEffect(() => {
    if (!open) return;
    const controller = new AbortController();
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch('/api/fastapi/admin/users-and-invites', {
          credentials: 'include',
          signal: controller.signal
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        const { users: u, invitations: invs } = normalizeUsersAndInvites(data);
        setUsers(u);
        setInvitations(invs);
      } catch (err) {
        setError(`Could not fetch admin data: ${err.message || String(err)}`);
      } finally {
        setLoading(false);
      }
    }
    load();
    return () => controller.abort();
  }, [open]);

  const inviteColumns = useMemo(() => {
    const pref = ['email', 'created_at', 'expires_at', 'status', 'organization', 'organization_role', 'used_by'];
    const keys = new Set();
    invitations.forEach(inv => Object.keys(inv || {}).forEach(k => keys.add(k)));
    keys.delete('code');
    const ordered = pref.filter(k => keys.has(k));
    if (ordered.length > 0) return ordered;
    return Array.from(keys).slice(0, 6);
  }, [invitations]);

  const userColumns = useMemo(() => {
    const pref = ['id', 'email', 'name', 'full_name', 'system_role', 'organization', 'organization_role', 'created_at'];
    const keys = new Set();
    users.forEach(u => Object.keys(u || {}).forEach(k => keys.add(k)));
    const ordered = pref.filter(k => keys.has(k));
    if (ordered.length > 0) return ordered;
    return Array.from(keys).slice(0, 8);
  }, [users]);

  async function submitInvite(e) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const payload = {
        email: form.email.trim(),
        organization: form.organization.trim() || undefined,
        organization_role: form.organizationRole.trim() || undefined,
        expires_in_days: Number(form.expiresInDays) || undefined
      };
      const res = await fetch('/api/fastapi/admin/invitations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });
      if (!res.ok) {
        const text = await res.text();
        // Fallback för backend som förväntar query-param "data" istället för JSON-body
        if (res.status === 422 && text.includes('"loc":["query","data"]')) {
          const qsUrl = '/api/fastapi/admin/invitations?data=' + encodeURIComponent(JSON.stringify(payload));
          const res2 = await fetch(qsUrl, { method: 'POST', credentials: 'include' });
          if (!res2.ok) {
            const text2 = await res2.text();
            throw new Error(`HTTP ${res2.status}: ${text2}`);
          }
        } else {
          throw new Error(`HTTP ${res.status}: ${text}`);
        }
      }
      // Refresh after successful creation
      const listRes = await fetch('/api/fastapi/admin/users-and-invites', { credentials: 'include' });
      const data = await listRes.json();
      const { users: u2, invitations: invs2 } = normalizeUsersAndInvites(data);
      setUsers(u2);
      setInvitations(invs2);
      setForm({ email: '', organization: '', organizationRole: '', expiresInDays: 14 });
    } catch (err) {
      setError(`Could not create invitation: ${err.message || String(err)}`);
    } finally {
      setSubmitting(false);
    }
  }

  if (!open) return null;

  const overlayStyle = {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.55)',
    zIndex: 20000,
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    overflowY: 'auto',
    padding: '24px'
  };

  const panelStyle = {
    width: '100%',
    maxWidth: 1100,
    background: isDark ? '#1f1f1f' : '#ffffff',
    color: isDark ? '#f3f3f3' : '#212529',
    borderRadius: 12,
    boxShadow: '0 8px 30px rgba(0,0,0,0.35)',
    padding: 16
  };

  const headerStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    marginBottom: 8,
    borderBottom: `1px solid ${isDark ? '#333' : '#e5e5e5'}`,
    paddingBottom: 8
  };

  const sectionStyle = { marginTop: 12 };

  return (
    <div style={overlayStyle} onClick={onClose} role="dialog" aria-modal="true" aria-label="Admin panel">
      <div style={panelStyle} onClick={e => e.stopPropagation()}>
        <div style={headerStyle}>
          <h3 style={{ margin: 0 }}>Admin - Users &amp; Invitations</h3>
          <button type="button" className="icon-btn" onClick={onClose} aria-label="Close">
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>

        {error && (
          <div style={{ marginBottom: 12, color: isDark ? '#ffb3b3' : '#b00020' }}>
            {error}
          </div>
        )}

        <form onSubmit={submitInvite} style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'flex-end', marginBottom: 12 }}>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <label htmlFor="invite-email">Email</label>
            <input
              id="invite-email"
              type="email"
              required
              value={form.email}
              onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
              placeholder="name@example.com"
              style={{ padding: 8, minWidth: 240, borderRadius: 6, border: `1px solid ${isDark ? '#444' : '#ccc'}`, background: 'transparent', color: 'inherit' }}
            />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <label htmlFor="invite-org">Organization</label>
            <input
              id="invite-org"
              type="text"
              value={form.organization}
              onChange={e => setForm(f => ({ ...f, organization: e.target.value }))}
              placeholder="Organization"
              style={{ padding: 8, minWidth: 200, borderRadius: 6, border: `1px solid ${isDark ? '#444' : '#ccc'}`, background: 'transparent', color: 'inherit' }}
            />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <label htmlFor="invite-orgrole">Organization role</label>
            <input
              id="invite-orgrole"
              type="text"
              value={form.organizationRole}
              onChange={e => setForm(f => ({ ...f, organizationRole: e.target.value }))}
              placeholder="Role within the organization"
              style={{ padding: 8, minWidth: 200, borderRadius: 6, border: `1px solid ${isDark ? '#444' : '#ccc'}`, background: 'transparent', color: 'inherit' }}
            />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <label htmlFor="invite-exp">Validity (days)</label>
            <input
              id="invite-exp"
              type="number"
              min="1"
              value={form.expiresInDays}
              onChange={e => setForm(f => ({ ...f, expiresInDays: e.target.value }))}
              style={{ padding: 8, width: 120, borderRadius: 6, border: `1px solid ${isDark ? '#444' : '#ccc'}`, background: 'transparent', color: 'inherit' }}
            />
          </div>
          <button type="submit" className="pill-btn pill-btn-primary" disabled={submitting} aria-busy={submitting}>
            <span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: 18, verticalAlign: 'middle' }}>send</span>{' '}
            {submitting ? 'Sending…' : 'Send invitation'}
          </button>
        </form>

        <div style={sectionStyle}>
          <h4 style={{ marginBottom: 6 }}>Invitations</h4>
          {loading ? (
            <div>Loading invitations…</div>
          ) : invitations.length === 0 ? (
            <div>No invitations yet.</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    {inviteColumns.map(col => (
                      <th
                        key={col}
                        style={{
                          textAlign: 'left',
                          borderBottom: `1px solid ${isDark ? '#333' : '#ddd'}`,
                          padding: '6px 8px',
                          background: isDark ? '#262626' : '#f5f7fb'
                        }}
                      >
                        {INVITE_LABELS[col] || col}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {invitations.map((inv, idx) => {
                    const email = getFirst(inv, ['email', 'to_email', 'to']);
                    const created = formatDate(getFirst(inv, ['created_at', 'created', 'createdAt', 'created_on']));
                    const expires = formatDate(getFirst(inv, ['expires_at', 'expires', 'expiresAt', 'expires_on']));
                    const code = getFirst(inv, ['code', 'invite_code']);
                    const org = getFirst(inv, ['organization', 'org']);
                    const orgRole = getFirst(inv, ['organization_role', 'organizationRole']);
                    const rawStatus = getFirst(inv, ['status'], inv?.expires_at ? (new Date(inv.expires_at) < new Date() ? 'expired' : 'active') : '');
                    const status = STATUS_LABELS[rawStatus] || rawStatus;
                    const usedBy = getFirst(inv, ['used_by', 'used_by_user_id', 'user_email', 'user']);
                    const derived = { email, code, created_at: created, expires_at: expires, status, organization: org, organization_role: orgRole, used_by: usedBy };
                    return (
                      <tr
                        key={idx}
                        style={{ background: idx % 2 ? (isDark ? '#1b1b1b' : '#fafafa') : 'transparent' }}
                      >
                        {inviteColumns.map(col => {
                          const statusColor =
                            ({ active: '#2e7d32', expired: '#b00020', consumed: '#9c27b0', inactive: '#616161' }[rawStatus]) ||
                            (isDark ? '#bbb' : '#666');
                          const cell = col === 'status'
                            ? <span style={{ padding: '2px 8px', borderRadius: 999, border: `1px solid ${statusColor}`, color: statusColor, fontSize: 12 }}>{derived.status}</span>
                            : (derived[col] ?? String(inv[col] ?? ''));
                          return (
                            <td
                              key={col}
                              style={{
                                borderBottom: `1px solid ${isDark ? '#333' : '#f0f0f0'}`,
                                padding: '6px 8px',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {cell}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div style={sectionStyle}>
          <h4 style={{ marginBottom: 6 }}>Users</h4>
          {loading ? (
            <div>Loading users…</div>
          ) : users.length === 0 ? (
            <div>No users found.</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    {userColumns.map(col => (
                      <th
                        key={col}
                        style={{
                          textAlign: 'left',
                          borderBottom: `1px solid ${isDark ? '#333' : '#ddd'}`,
                          padding: '6px 8px',
                          background: isDark ? '#262626' : '#f5f7fb'
                        }}
                      >
                        {USER_LABELS[col] || col}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {users.map((u, idx) => (
                    <tr
                      key={idx}
                      style={{ background: idx % 2 ? (isDark ? '#1b1b1b' : '#fafafa') : 'transparent' }}
                    >
                      {userColumns.map(col => (
                        <td key={col} style={{ borderBottom: `1px solid ${isDark ? '#333' : '#f0f0f0'}`, padding: '6px 8px', whiteSpace: 'nowrap' }}>
                          {String(u[col] ?? '')}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
